import { Component, computed, effect, inject, input, OnDestroy, OnInit, output, signal } from "@angular/core";
import { UiKitModule } from "@app/ui-kit.module";
import { FilterProductComponent } from "@components/filter-product/filter-product.component";
import { RefreshProductsHintComponent } from "@components/refresh-products-hint/refresh-products-hint.component";
import { FgdsProductType, FullProductType, Product } from "@domain/product/product";
import { ProductService } from "@domain/product/product.service";
import { FgdsProject as Project } from "@domain/project/fgds-project";
import { GasWarningCenterCardComponent } from "@project/gas-warning-center-configuration/gas-warning-center-card/gas-warning-center-card.component";
import { GasWarningCenterType } from "@project/gas-warning-center-configuration/gas-warning-center-type";
import { ProductWithQuantity } from "@project/gas-warning-center-configuration/product-with-quantity";
import { BehaviorSubject, combineLatest, map, ReplaySubject } from "rxjs";
import { take } from "rxjs/operators";

@Component({
  selector: "app-docking-station-selector",
  imports: [UiKitModule, GasWarningCenterCardComponent, FilterProductComponent, RefreshProductsHintComponent],
  templateUrl: "./docking-station-selector.component.html",
  standalone: true,
  styleUrl: "./docking-station-selector.component.scss",
})
export class DockingStationSelectorComponent implements OnInit, OnDestroy {
  private productService = inject(ProductService);

  readonly project = input.required<Project>();
  readonly type = input.required<GasWarningCenterType>();
  readonly originalDockingStations = input<ProductWithQuantity[]>();
  readonly selectedModules = input<{
    inputModules: ProductWithQuantity[];
    relayModules: ProductWithQuantity[];
    gatewayModules: ProductWithQuantity[];
  }>();

  readonly dockingStationsSelect$ = output<ProductWithQuantity[]>();
  readonly isSelectionValid$ = output<boolean>();
  readonly necessarySlotCovers$ = output<ProductWithQuantity>();

  protected readonly isSelectionValid = computed(() => {
    return this.availableSlots() >= this.requiredSlots();
  });
  protected readonly infoMessage = computed(
    () =>
      $localize`:@@gasWarningCenterConfiguration.selectGasWarningCenter.dockingStations.infoMessage:Mindestanzahl benötigter Slots: ${this.requiredSlots()}:requiredSlots:`,
  );
  protected readonly filteredDockingStations$ = new ReplaySubject<Product[]>(1);
  protected preselectedDockingStations: ProductWithQuantity[] = [];

  private readonly selectedDockingStations = signal<ProductWithQuantity[]>([]);
  private readonly availableSlots = computed(() => this.calculateAvailableSlots(this.selectedDockingStations()));
  private readonly requiredSlots = computed(() => {
    const selectedModules = this.selectedModules();
    if (!selectedModules) {
      return 0;
    }
    return this.calculateRequiredSlots(selectedModules);
  });
  private readonly emptySlots = computed(() => {
    if (!this.isSelectionValid()) {
      return 0;
    }
    return this.availableSlots() - this.requiredSlots();
  });

  private readonly dockingStations$ = new ReplaySubject<Product[]>(1);
  private readonly searchValue$ = new BehaviorSubject<string | null>(null);
  private slotCoverProduct = signal<Product | undefined>(undefined);

  constructor() {
    combineLatest([this.searchValue$, this.dockingStations$]).subscribe(([searchValue, dockingStations]) => {
      if (searchValue) {
        dockingStations = dockingStations.filter((product) => this.productService.searchFilter(product, searchValue));
      }
      this.filteredDockingStations$.next(dockingStations);
    });

    effect(() => {
      const originalDockingStations = this.originalDockingStations();
      if (!originalDockingStations?.length) {
        return;
      }
      this.updatePreselectedDockingStations(originalDockingStations);
    });

    effect(() => {
      const type = this.type();
      this.collectDockingStations(type);
    });

    effect(() => {
      this.isSelectionValid$.emit(this.isSelectionValid());
    });

    effect(() => {
      const product = this.slotCoverProduct();
      if (!product) {
        return;
      }
      const quantity = this.emptySlots();
      const necessarySlotCovers = { product, quantity };
      this.necessarySlotCovers$.emit(necessarySlotCovers);
    });
  }

  ngOnInit() {
    this.productService
      .getProductById("8323812")
      .pipe(take(1))
      .subscribe((product) => {
        this.slotCoverProduct.set(product);
      });
  }

  ngOnDestroy() {
    this.dockingStations$.complete();
    this.filteredDockingStations$.complete();
    this.searchValue$.complete();
  }

  protected search(value: string) {
    this.searchValue$.next(value);
  }

  protected getSelectedQuantity(product: Product) {
    const selected = this.selectedDockingStations().find((dockingStation) => dockingStation.product.id === product.id);
    return selected?.quantity ?? 0;
  }

  protected getPreselectedQuantity(product: Product) {
    const preselected = this.preselectedDockingStations.find((dockingStation) => dockingStation.product.id === product.id);
    return preselected?.quantity ?? 0;
  }

  protected updateSelectedDockingStations(quantity: number, dockingStation: Product) {
    const current = this.selectedDockingStations().find((selected) => selected.product.id === dockingStation.id);
    if ((quantity === 0 && !current) || (quantity > 0 && current?.quantity === quantity)) {
      return;
    }
    const updated = this.selectedDockingStations().filter((selected) => selected.product.id !== dockingStation.id);
    if (quantity > 0) {
      updated.push({ product: dockingStation, quantity });
    }
    this.selectedDockingStations.set(updated);
    this.dockingStationsSelect$.emit(updated);
  }

  private updatePreselectedDockingStations(originalDockingStations: ProductWithQuantity[]) {
    this.dockingStations$.pipe(take(1)).subscribe((dockingStations) => {
      this.preselectedDockingStations = originalDockingStations
        .map((original) => {
          const matchingProduct = dockingStations.find((product) => product.id === original.product.id);
          return matchingProduct ? { product: matchingProduct, quantity: original.quantity } : null;
        })
        .filter((item): item is ProductWithQuantity => item !== null && item.product.isAvailable);
    });
  }

  private collectDockingStations(type?: GasWarningCenterType) {
    if (!type) {
      this.dockingStations$.next([]);
      return;
    }
    this.productService
      .getAvailableProducts(FgdsProductType.GASWARNINGCENTER)
      .pipe(
        take(1),
        map((products) =>
          products.filter((p) =>
            [FullProductType.GASWARNINGCENTER_DS_8_SLOT, FullProductType.GASWARNINGCENTER_DS_4_SLOT].includes(p.fullType),
          ),
        ),
      )
      .subscribe((dockingStations) => {
        this.dockingStations$.next(dockingStations);
      });
  }

  private calculateAvailableSlots(
    selectedDockingStations: {
      product: Product;
      quantity: number;
    }[],
  ) {
    return selectedDockingStations.reduce((acc: number, cum: ProductWithQuantity) => {
      if (cum.product.fullType === FullProductType.GASWARNINGCENTER_DS_8_SLOT) {
        return acc + cum.quantity * 8;
      }
      if (cum.product.fullType === FullProductType.GASWARNINGCENTER_DS_4_SLOT) {
        return acc + cum.quantity * 4;
      }
      return acc + cum.quantity;
    }, 0);
  }

  private calculateRequiredSlots(selectedModules: {
    inputModules: ProductWithQuantity[];
    relayModules: ProductWithQuantity[];
    gatewayModules: ProductWithQuantity[];
  }) {
    return Object.values(selectedModules)
      .flat()
      .reduce((sum, { quantity }) => sum + quantity, 0);
  }
}
